import * as ngCore from '@angular/core'
import * as ents from '@attain-apps/entities'
import * as ec from '@attain-apps/fe/web/main/lib/entity-collection'
import * as rxjs from 'rxjs'

import * as contexts from '../contexts'

import * as gql from './gql'
import * as models from './models'
import type { TypeParams } from './types'

/**
 * Provides API operations for Admin Center user tables and forms.
 */
@ngCore.Injectable({ providedIn: 'root' })
export class Api implements ec.models.Api<TypeParams> {
  /** @see {@link contexts.Feature} */
  private readonly _contexts = ngCore.inject(contexts.Feature)

  // TODO: Pagination by cursor should be implemented. For now, just ignore
  // cursor and use only items from response
  /** @inheritdoc */
  public list = ec.models.Api.list.using.onlyFilter<TypeParams>(
    (_, { filter }) => {
      if (!filter?.additionalFilters) {
        return gql.queries.users
          .execute()
          .pipe(rxjs.map(response => response.items))
      }
      const { appId, scenarioId, tenantId, zoneId } = filter.additionalFilters
      return (
        scenarioId && appId && zoneId && tenantId
          ? gql.queries.inScenario.execute({
              appId,
              scenarioId,
              tenantId,
              zoneId,
            })
          : appId && zoneId && tenantId
            ? gql.queries.inApp.execute({
                appId,
                tenantId,
                zoneId,
              })
            : zoneId && tenantId
              ? gql.queries.inZone.execute({
                  tenantId,
                  zoneId,
                })
              : tenantId
                ? gql.queries.inTenant.execute({
                    tenantId,
                  })
                : gql.queries.users.execute()
      ).pipe(rxjs.map(response => response.items))
    },
  )

  /** @inheritdoc */
  public create: ec.models.Api<TypeParams>['create'] = (_, input) => {
    const context = this._contexts.selectors.activeEntityContext.asSignal()
    return gql.mutations.createUser.executor({
      info: gql.inputs.user.INPUT.getGqlFrom(input),
      tenantContextId: context?.tenantId,
    })
  }

  /** @inheritdoc */
  public update: ec.models.Api<TypeParams>['update'] = (_, { id, updates }) =>
    gql.mutations.updateUser.executor({
      updateUserId: id,
      updates: gql.inputs.user.UPDATES.getGqlFrom(updates),
    })

  /** @inheritdoc */
  public delete: ec.models.Api<TypeParams>['delete'] = (_, id) =>
    gql.mutations.deleteUser.executor({
      deleteUserId: id,
    })

  /** @inheritdoc */
  public getAttributes: ec.models.Api<TypeParams>['getAttributes'] = () =>
    rxjs.of([
      ec.models.Attribute.fromKey<TypeParams>('id', { name: 'ID' }),
      ec.models.Attribute.fromKey<TypeParams>('firstName', {
        name: 'First Name',
      }),
      ec.models.Attribute.fromKey<TypeParams>('lastName', {
        name: 'Last Name',
      }),
      ec.models.Attribute.fromKey<TypeParams>('jobTitle', {
        name: 'Job Title',
      }),
      ec.models.Attribute.fromKey<TypeParams>('email', { name: 'Email' }),
      {
        getValueFrom: user => user.isPlatformAdmin === true,
        id: 'isPlatformAdmin',
        name: 'Is Platform Admin',
        valueInfo: {
          type: ents.core.common.value.types.Intrinsic.Boolean,
        },
      },
      {
        getValueFrom: models.User.getAccessFlagsLabel,
        id: models.User.ACCESS_FLAGS_ATTRIBUTE_ID,
        isHidden:
          this._contexts.selectors.activeEntityContext.asSignal() === undefined,
        name: 'Access Flags',
      },
    ])
}
